import SubsetCandidateScreenModal from '@/components/Base/modals/SubsetCandidateScreenModal'
import { defaultAgGridSidebarProps } from '@/constants/ag-grid-scenario-builder.constant'
import { formatDisplayValue } from '@/constants/common.constant'
import { useAppSelector } from '@/hooks/use-app-selector'
import { resetSubsetScreening } from '@/states/candidateScreen.slice'
import { themeClassName } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Add, ArrowForward, CheckCircle, Clear, Search } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

interface Security {
  id: string
  securityId: string
  description: string
  coupon: string
  price: string
  country: string
  currency: string
  rating: string
  maturityDate: string
  sector: string
  outstanding: string
}

interface AddSecuritiesProps {
  securities?: Security[]
  onAddSecurities?: (securities: Security[]) => void
  onTransferToWhatIf?: (securities: Security[]) => void
  onClearSecurities?: () => void
  loading?: boolean
  onOpenModal?: () => void
}

const AddSecurities: React.FC<AddSecuritiesProps> = ({
  securities = [],
  onAddSecurities,
  onTransferToWhatIf,
  onClearSecurities,
  loading = false,
  onOpenModal
}) => {
  const dispatch = useDispatch()
  const [searchTerm, setSearchTerm] = useState('')
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [selectedSecurities, setSelectedSecurities] = useState<Security[]>([])
  const [subsetModalOpen, setSubsetModalOpen] = useState(false)
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null)
  
  // Redux state access - same as scenario template
  const candidateScreenState = useAppSelector((s) => s.candidateScreen)

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '',
      field: 'selected',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
      lockPosition: 'left',
      suppressMenu: true,
      sortable: false,
      filter: false
    },
    {
      headerName: 'Security ID',
      field: 'securityId',
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'left' }
    },
    {
      headerName: 'Coupon',
      field: 'coupon',
      flex: 1,
      minWidth: 100,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      cellStyle: { textAlign: 'right' },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') return ''
        const num = parseFloat(params.value)
        if (isNaN(num)) return params.value
        return num.toFixed(2)
      }
    },
    {
      headerName: 'Price',
      field: 'price',
      flex: 1,
      minWidth: 100,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'Country',
      field: 'country',
      flex: 1,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'right' },
      hide: true
    },
    {
      headerName: 'Currency',
      field: 'currency',
      flex: 1,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'right' },
      hide: true
    },
    {
      headerName: 'Rating',
      field: 'rating',
      flex: 1,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'Maturity Date',
      field: 'maturityDate',
      flex: 1,
      minWidth: 120,
      filter: 'agDateColumnFilter',
      cellStyle: { textAlign: 'right' }
    }
  ], [])

  // Default column properties
  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    suppressMenu: false,
    floatingFilter: false
  }), [])

  // Grid options
  const gridOptions = useMemo(() => ({
    rowSelection: 'multiple' as const,
    suppressRowClickSelection: true,
    suppressCellFocus: true,
    enableCellTextSelection: true,
    animateRows: true,
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: [50, 100, 200, 500],
    suppressPaginationPanel: false,
    suppressRowHoverHighlight: false,
    suppressMenuHide: false,
    suppressContextMenu: true,
    cellSelection: false,
    suppressMultiRangeSelection: true,
    suppressCopyRowsToClipboard: true,
    suppressCopySingleCellRanges: true,
    suppressFillHandle: true,
    suppressRowTransform: true,
    sideBar: defaultAgGridSidebarProps,
    getRowId: (params: any) => params.data.id,
    onRowDataChanged: () => {
      if (gridApi) {
        gridApi.sizeColumnsToFit()
      }
    }
  }), [gridApi])

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api)
    params.api.sizeColumnsToFit()
  }, [])

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows()
    setSelectedSecurities(selectedRows)
  }, [])

  // Handle cell value changes to ensure What-If values are captured
  const onCellValueChanged = useCallback((event: any) => {
    const updatedData = event.api.getRowNode(event.node.id)?.data
    if (updatedData) {
      const selectedRows = event.api.getSelectedRows()
      setSelectedSecurities([...selectedRows])
    }
  }, [])

  // Handle search
  const handleSearch = useCallback((searchValue: string) => {
    setSearchTerm(searchValue)
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', searchValue)
    }
  }, [gridApi])

  // Handle add selected securities to What-If panel
  const handleAddSelected = useCallback(() => {
    if (selectedSecurities.length > 0 && onTransferToWhatIf) {
      onTransferToWhatIf(selectedSecurities)
      
      // Clear selection after adding
      if (gridApi) {
        gridApi.deselectAll()
      }
      setSelectedSecurities([])
    }
  }, [selectedSecurities, onTransferToWhatIf, gridApi])

  // Handle modal close - access screening securities from global state if available
  const handleModalClose = useCallback(() => {
    setSubsetModalOpen(false)
    
    // The SubsetCandidateScreen component might store securities data globally
    const globalSecurities = (window as any).__SCREENING_SECURITIES__ || []
    
    if (globalSecurities.length > 0 && onAddSecurities) {
      const screenedSecurities: Security[] = globalSecurities.map((security: any, index: number) => ({
        id: security.sec_id || security.secId || security.id || `security-${Date.now()}-${index}`,
        securityId: security.sec_id || security.secId || security.securityId || `SEC-${index}`,
        description: security.description || formatDisplayValue(null),
        coupon: security.coupon || formatDisplayValue(null),
        price: security.price ? parseFloat(security.price).toFixed(2) : formatDisplayValue(security.price),
        country: formatDisplayValue(security.country),
        currency: formatDisplayValue(security.currency),
        rating: formatDisplayValue(security.moody || security.rating),
        maturityDate: security.maturity ? String(security.maturity).substring(0, 10) : security.maturityDate ? String(security.maturityDate).substring(0, 10) : formatDisplayValue(null),
        sector: formatDisplayValue(security.sector),
        outstanding: formatDisplayValue(security.outstanding)
      }))
      
      onAddSecurities(screenedSecurities)
      
      // Clear the global securities after use
      delete (window as any).__SCREENING_SECURITIES__
    }
    
    dispatch(resetSubsetScreening())
  }, [dispatch, onAddSecurities])

  // Row data with unique IDs
  const rowData = useMemo(() => {
    return securities.map((security, index) => ({
      ...security,
      id: security.id || `security-${index}`
    }))
  }, [securities])

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      backgroundColor: 'white',
      position: 'relative'
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#000000' }}>
            Securities
          </Typography>
          {selectedSecurities.length > 0 && (
            <Chip 
              label={`${selectedSecurities.length} selected`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Typography variant="body2" sx={{ color: '#666', mx: 1 }}>
            |
          </Typography>
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={() => setSubsetModalOpen(true)}
            size="small"
            sx={{ 
              color: '#1976d2 !important',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 400,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              },
              '&.Mui-disabled': {
                color: '#1976d2 !important'
              }
            }}
          >
            Add Securities
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Transfer buttons */}
          <Button
            variant="contained"
            startIcon={<ArrowForward />}
            onClick={handleAddSelected}
            disabled={selectedSecurities.length === 0 || !onTransferToWhatIf}
            size="small"
            sx={{ 
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Add to What-If Analysis ({selectedSecurities.length})
          </Button>
          <TextField
            size="small"
            placeholder="Search securities..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
      </Box>

      {/* Success Message */}
      <Collapse in={!!transferSuccess}>
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ 
            mx: 1.5, 
            mb: 1,
            borderRadius: 1
          }}
        >
          {transferSuccess}
        </Alert>
      </Collapse>

      {/* AG-Grid */}
      <Box sx={{ flex: 1, width: '100%', position: 'relative' }} className={themeClassName}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onCellValueChanged={onCellValueChanged}
          modules={[ClientSideRowModelModule]}
          loading={loading}
          loadingOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography>Loading securities to What-If Panel...</Typography>
            </Box>
          )}
          noRowsOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="textSecondary">
                {searchTerm ? `No securities found matching "${searchTerm}"` : 'No securities available'}
              </Typography>
            </Box>
          )}
        />
      </Box>

      {/* Footer with Clear button aligned to grid */}
      <Box sx={{ 
        borderTop: '1px solid #e0e0e0',
        p: 1,
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: 'white'
      }}>
        <Button
          variant="outlined"
          startIcon={<Clear />}
          onClick={onClearSecurities}
          disabled={securities.length === 0 || !onClearSecurities}
          size="small"
          sx={{ 
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          Clear All Securities
        </Button>
      </Box>

      {/* What-If Screening Modal - Uses onApply callback for securities data */}
      <SubsetCandidateScreenModal
        isOpen={subsetModalOpen}
        onClose={handleModalClose}
        isSubset={true}
        onApply={handleModalClose}
      />
    </Box>
  )
}

export default AddSecurities
